import getUsedLocale from '@/utils/get-used-locale'
import { State } from '@/store/user/state'
import JwtDecode from 'jwt-decode'
import { Event } from '@/types/Event.type'
import { UserTokenDecoded } from '@/types/UserTokenDecoded.type'
import { UserRole } from '@/types/UserRole.type'
import { UserFeature } from '@/types/UserFeature.type'

export function isAdmin(state: State): boolean {
    if (state.decodedToken?.roles) {
        return !!state.decodedToken.roles.find((role: string) => ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN'].includes(role))
    }

    return false
}

export function isAmbassador(state: State): boolean {
    if (state.decodedToken?.roles) {
        return !!state.decodedToken.roles.find((role: string) => ['ROLE_AMBASSADOR'].includes(role))
    }

    return false
}

export function isSwitchedToMember(): boolean {
    return window.__isJwtSwitch
}

export function isLead(state: State): boolean {
    return state.decodedToken?.type === 'lead'
}

export function isCommunity(state: State): boolean {
    if (state.decodedToken?.roles) {
        return !!state.decodedToken.roles.find((role: string) => ['ROLE_BL_COMMUNITY'].includes(role))
    }

    return false
}

export function isLeClub(state: State, getters: any): boolean {
    return getters.hasRole('ROLE_BL_LECLUB')
}

export function isLostMember(state: State, getters: any): boolean {
    if (getters.isAdmin) {
        return false
    } else {
        return (
            (
                getters.hasRole('ROLE_BL_LECLUB_LOST') || 
                getters.hasRole('ROLE_BL_LECLUB_EXPERT_LOST') ||
                getters.hasRole('ROLE_BL_LECLUB_PARTNER_LOST')
            )  && !getters.hasRole('ROLE_BL_LECLUB')
        )
    }
}

export function isClubProximity(state: State): boolean {
    if (state.decodedToken?.roles) {
        return !!state.decodedToken.roles.find((role: string) => ['ROLE_BL_LECLUB_PROXIMITY'].includes(role))
    }

    return false
}

/**
 * Detect if a logged in user is a partner
 */
export function isPartner(state: State, getters: any): boolean {
    const tokenDecoded: UserTokenDecoded | null = getters.jwtTokenDecoded

    if (!tokenDecoded?.roles?.length) {
        return false
    }

    return tokenDecoded.roles.includes('ROLE_BL_LECLUB_PARTNER_CONTACT')
}

export function hasRole(state: State, getters: any): (role: UserRole, jwtDecoded?: UserTokenDecoded) => boolean {
    return (role: UserRole, jwtDecoded?: UserTokenDecoded) => {
        const tokenDecoded: UserTokenDecoded | null = jwtDecoded || getters.jwtTokenDecoded

        if (tokenDecoded?.roles) {
            if (tokenDecoded.roles.includes('ROLE_ADMIN') || tokenDecoded.roles.includes('ROLE_SUPER_ADMIN')) {
                return true
            }

            return !!tokenDecoded.roles.find((myRole: string) => myRole === role)
        }

        return false
    }
}

export const isSpeaker = (state: State, getters: any) => (event: Event): boolean => {
    return (
        !!event &&
        !!event.speakers.find(speaker =>
            getters.isLead ? speaker.lead?.id : speaker.member?.id === getters.jwtTokenDecoded?.id
        )
    )
}

export function isConnected(state: State): boolean {
    return !!state.token
}

export function getLocale(state: State) {
    return (state.me && 'locale' in state.me && state.me?.locale) || getUsedLocale()
}

// Use this instead of `state.decodedToken`
export function jwtTokenDecoded(state: State): UserTokenDecoded | null {
    if (!state.token) {
        return null
    }

    return JwtDecode(state.token)
}

export function getFeatures(state: State, getters: any): UserFeature {
    const isTripPlanner = getters.hasRole('ROLE_FEATURE_TRIPPLANNER')
    const isWorkspace = getters.hasRole('ROLE_FEATURE_WORKSPACE')

    return {
        isTripPlanner,
        isWorkspace,
    }
}
